// ./src/utils/stripeDashboard.ts
//
// THE ONE PLACE THAT BUILDS A STRIPE DASHBOARD DEEP LINK.
//
// ⚠⚠ WHY IT IS SHARED. There were two implementations — the payments page's and the sponsorship
// application drawer's — and they DISAGREED about the dangerous case. The payments one treated a
// missing connected account as "just leave that segment out":
//
//     const acct = p.processorAccountId ? `/${p.processorAccountId}` : "";
//     return `https://dashboard.stripe.com${acct}${testSeg}/payments/${pi}`;
//
// which produces `https://dashboard.stripe.com/test/payments/pi_…` — **Events Magic's OWN Stripe
// dashboard**, rendered to a host as "View in Stripe". Its own comment warns about exactly that and
// then gates on `direction` instead, so a `host_revenue` row whose account id is null (which is
// legitimate: a sponsorship row is written from the webhook, where the connected account is resolved
// best-effort) still produced the bad link.
//
// ⚠ BOTH GUARDS, NOT EITHER. `direction` catches a platform purchase, where the money settled on our
// account by design; the account id catches every other way of ending up without one. Dropping
// either is how this link starts pointing at our dashboard again.
//
// ⚠ NO LINK IS A FINE ANSWER. Every caller says "No Stripe link available" rather than rendering a
// link it cannot make safely.
/**
 * ⚠ THE ONE VALUE THIS COMPARES AGAINST, DECLARED LOCALLY AND ON PURPOSE.
 *
 * `PAYMENT_DIRECTION` lives beside `PaymentService`, whose import chain reaches `@/config/apollo`
 * and Vite's `import.meta.env` — so importing it would make this module unloadable outside a
 * browser build, and this rule is precisely the one worth RUNNING in a guard rather than grepping.
 * (It had never been run, and the two copies of it disagreed for months.)
 *
 * ⚠ The fork is pinned: `check-ledger` asserts this string still matches the vocabulary in
 * `PaymentService.ts`, so the day somebody renames it there, the guard says so.
 */
const PLATFORM_REVENUE = "platform_revenue";

/** The minimum a row needs to be linkable — satisfied by `Payment` and `PartnerPayment` alike. */
export interface StripeLinkable {
  processor?: string | null;
  processorPaymentId?: string | null;
  processorAccountId?: string | null;
  direction?: string | null;
  isTest?: boolean | null;
}

/**
 * A deep link to ONE transaction on the HOST's connected account, or null.
 *
 * ⚠ A refund's id is an `re_…` and a charge's is a `pi_…`; Stripe resolves both from `/payments/`,
 * so this deliberately does not require a `pi_` prefix — the sponsorship drawer links refunds too.
 */
export const stripeDashboardUrl = (payment: StripeLinkable | null | undefined): string | null => {
  const id = payment?.processorPaymentId;

  if (!payment || payment.processor !== "stripe" || !id) {
    return null;
  }

  // The host paid US: it settled on our platform account, and there is nothing of theirs to show.
  if (payment.direction === PLATFORM_REVENUE) {
    return null;
  }

  if (!payment.processorAccountId) {
    return null;
  }

  return `https://dashboard.stripe.com/${payment.processorAccountId}${payment.isTest ? "/test" : ""}/payments/${id}`;
};
